import React, { useEffect, useState } from 'react';
import { createPortal } from 'react-dom';
import { Link, NavLink, Outlet, useLocation, useNavigate } from 'react-router-dom';
import { ChevronDown, Menu, X } from 'lucide-react';
import { useAuth } from '../../contexts/AuthContext';
import {
  EDUCATION_FOCUS_REDIRECT,
  isRidesModuleEnabled,
  isSportsModuleEnabled,
} from '../../config/modules';
import BrandLogo from '../brand/BrandLogo';
import SharedFooter from './SharedFooter';

type MenuItem = {
  label: string;
  to: string;
  end?: boolean;
};

type MenuGroup = {
  label: string;
  items: MenuItem[];
};

const allPublicMenuGroups: MenuGroup[] = [
  {
    label: 'Education',
    items: [
      { label: 'Education Home', to: '/education', end: true },
      { label: 'Find Tutors', to: '/education/tutors' },
      { label: 'Classes', to: '/education/classes' },
      { label: 'Competitive Exams', to: '/education/competitive-exams' },
      { label: 'Exam Portal', to: '/exams' },
      { label: 'For Teachers', to: '/teacher' },
    ],
  },
  {
    label: 'Books',
    items: [
      { label: 'Books Home', to: '/books', end: true },
      { label: 'Search Books', to: '/books/search' },
      { label: 'Book Shops', to: '/book-shops' },
      { label: 'Sell Books', to: '/books/sell' },
      { label: 'Exchange Books', to: '/books/exchange' },
      { label: 'Donate Books', to: '/books/donate' },
      { label: 'Seller Registration', to: '/book-shop/register' },
    ],
  },
  {
    label: 'Sports',
    items: [
      { label: 'Sports Home', to: '/sports', end: true },
      { label: 'Sports Registration', to: '/sports/register' },
      { label: 'Register Player', to: '/sports/register-player' },
      { label: 'Create Team', to: '/sports/create-team' },
      { label: 'Find Teams', to: '/sports/teams' },
      { label: 'Tournaments', to: '/sports/tournaments' },
      { label: 'Create Tournament', to: '/sports/tournaments/create' },
      { label: 'Grounds', to: '/sports/grounds' },
    ],
  },
  {
    label: 'Rides',
    items: [
      { label: 'Rides Home', to: '/rides', end: true },
      { label: 'Book Ride', to: '/rides/book' },
      { label: 'Rides Registration', to: '/rides/register' },
      { label: 'Register Driver', to: '/rides/driver/register' },
      { label: 'Register Stand', to: '/rides/stand/register' },
      { label: 'Nearby Stands', to: '/rides/stands' },
    ],
  },
  {
    label: 'Programs',
    items: [
      { label: 'Scholarship', to: '/scholarship' },
      { label: 'Jobs', to: '/jobs' },
    ],
  },
  {
    label: 'Company',
    items: [
      { label: 'About Us', to: '/about' },
      { label: 'Contact Us', to: '/contact' },
      { label: 'Account Deletion', to: '/account-deletion' },
    ],
  },
];

const publicMenuGroups = allPublicMenuGroups.filter((group) => {
  if (group.label === 'Sports') return isSportsModuleEnabled;
  if (group.label === 'Rides') return isRidesModuleEnabled;
  return true;
});

const isPathActive = (pathname: string, item: MenuItem) => {
  if (item.end) {
    return pathname === item.to;
  }

  return pathname === item.to || pathname.startsWith(`${item.to}/`);
};

const desktopPillClass = (isActive: boolean) =>
  `${isActive ? 'bg-slate-950 text-white shadow-sm' : 'text-slate-600 hover:bg-slate-100 hover:text-slate-900'} inline-flex items-center rounded-full px-4 py-2 text-sm font-medium transition`;

const DesktopMenuGroup: React.FC<{
  group: MenuGroup;
  pathname: string;
}> = ({ group, pathname }) => {
  const [isOpen, setIsOpen] = useState(false);
  const isGroupActive = group.items.some((item) => isPathActive(pathname, item));
  const menuStateClass = isOpen ? 'visible opacity-100' : 'invisible opacity-0';

  const closeMenu = () => {
    setIsOpen(false);
    if (document.activeElement instanceof HTMLElement) {
      document.activeElement.blur();
    }
  };

  return (
    <div
      className="relative"
      onMouseEnter={() => setIsOpen(true)}
      onMouseLeave={() => setIsOpen(false)}
      onFocus={() => setIsOpen(true)}
      onBlur={(event: React.FocusEvent<HTMLDivElement>) => {
        if (!event.currentTarget.contains(event.relatedTarget as Node | null)) {
          setIsOpen(false);
        }
      }}
    >
      <button type="button" className={desktopPillClass(isGroupActive)} aria-haspopup="true" aria-expanded={isOpen}>
        {group.label}
        <ChevronDown className="ml-1 h-4 w-4" aria-hidden="true" />
      </button>
      <div className={`${menuStateClass} absolute left-0 top-full z-50 w-64 pt-2 transition`}>
        <div className="max-h-[calc(100vh-6rem)] overflow-y-auto rounded-2xl border border-slate-200 bg-white p-2 shadow-xl shadow-slate-900/10">
          {group.items.map((item) => (
            <Link
              key={item.to}
              to={item.to}
              onClick={closeMenu}
              className={`${isPathActive(pathname, item) ? 'bg-slate-100 text-slate-950' : 'text-slate-700 hover:bg-slate-50 hover:text-slate-950'} block rounded-xl px-3 py-2 text-sm font-medium transition`}
            >
              {item.label}
            </Link>
          ))}
        </div>
      </div>
    </div>
  );
};

const MobileMenuGroup: React.FC<{
  group: MenuGroup;
  pathname: string;
  isOpen: boolean;
  onToggle: () => void;
  onNavigate: () => void;
}> = ({ group, pathname, isOpen, onToggle, onNavigate }) => {
  const isGroupActive = group.items.some((item) => isPathActive(pathname, item));

  return (
    <section className="rounded-xl border border-slate-200 bg-white shadow-sm">
      <button
        type="button"
        onClick={onToggle}
        className="flex min-h-[50px] w-full items-center justify-between gap-3 px-4 py-3 text-left"
        aria-expanded={isOpen}
      >
        <span className={`text-sm font-semibold ${isGroupActive ? 'text-slate-950' : 'text-slate-700'}`}>{group.label}</span>
        <ChevronDown className={`h-4 w-4 text-slate-500 transition ${isOpen ? 'rotate-180' : ''}`} aria-hidden="true" />
      </button>
      {isOpen && (
        <div className="border-t border-slate-100 px-2 py-2">
          {group.items.map((item) => (
            <Link
              key={item.to}
              to={item.to}
              onClick={onNavigate}
              className={`${isPathActive(pathname, item) ? 'bg-slate-950 text-white' : 'text-slate-700 hover:bg-slate-50 hover:text-slate-950'} block rounded-lg px-3 py-2.5 text-sm font-medium transition`}
            >
              {item.label}
            </Link>
          ))}
        </div>
      )}
    </section>
  );
};

const PublicLayout: React.FC = () => {
  const { user, logout } = useAuth();
  const navigate = useNavigate();
  const location = useLocation();
  const [isMobileMenuOpen, setIsMobileMenuOpen] = useState(false);
  const activeMobileGroup =
    publicMenuGroups.find((group) => group.items.some((item) => isPathActive(location.pathname, item)))?.label ||
    publicMenuGroups[0]?.label ||
    '';
  const [openMobileGroup, setOpenMobileGroup] = useState(activeMobileGroup);
  const roleDetails = user ? { user } : null;
  const dashboardPath =
    user?.role === 'TEACHER'
      ? '/teacher/dashboard'
      : user?.role === 'SELLER'
        ? '/shop/dashboard'
        : user?.role === 'SPORTS'
          ? isSportsModuleEnabled
            ? '/sports/dashboard'
            : EDUCATION_FOCUS_REDIRECT
          : user?.role === 'RIDES'
            ? isRidesModuleEnabled
              ? '/rides/dashboard'
              : EDUCATION_FOCUS_REDIRECT
          : user?.role === 'STUDENT'
            ? '/student/dashboard'
            : '/dashboard';

  const navLinkClass = ({ isActive }: { isActive: boolean }) => desktopPillClass(isActive);

  useEffect(() => {
    if (isMobileMenuOpen) {
      setOpenMobileGroup(activeMobileGroup);
    }
  }, [activeMobileGroup, isMobileMenuOpen]);

  useEffect(() => {
    if (!isMobileMenuOpen) return undefined;
    const previousOverflow = document.body.style.overflow;
    document.body.style.overflow = 'hidden';
    return () => {
      document.body.style.overflow = previousOverflow;
    };
  }, [isMobileMenuOpen]);

  const handleLogout = () => {
    logout();
    setIsMobileMenuOpen(false);
    navigate('/login');
  };

  const closeMobileMenu = () => setIsMobileMenuOpen(false);

  return (
    <div className="min-h-screen bg-[linear-gradient(180deg,_rgba(248,250,252,0.96)_0%,_rgba(241,245,249,0.88)_100%)]">
      <nav className="sticky top-0 z-[1200] border-b border-white/60 bg-white/90 backdrop-blur-xl">
        <div className="mx-auto max-w-7xl px-4 sm:px-6 lg:px-8">
          <div className="flex min-h-[84px] justify-between gap-4 py-3">
            <div className="flex items-center">
              <BrandLogo compact to={roleDetails?.user ? dashboardPath : '/'} />
              <div className="hidden lg:ml-8 lg:flex lg:flex-wrap lg:items-center lg:gap-2">
                {!roleDetails?.user ? (
                  <>
                    <NavLink to="/" end className={navLinkClass}>
                      Home
                    </NavLink>
                    {publicMenuGroups.map((group) => (
                      <DesktopMenuGroup key={group.label} group={group} pathname={location.pathname} />
                    ))}
                  </>
                ) : (
                  <>
                    <NavLink to={dashboardPath} className={navLinkClass}>
                      Dashboard
                    </NavLink>
                    {isRidesModuleEnabled && (
                      <NavLink to="/rides/book" className={navLinkClass}>
                        Book Ride
                      </NavLink>
                    )}
                  </>
                )}
              </div>
            </div>

            <div className="hidden lg:flex lg:items-center lg:gap-3">
              {!roleDetails?.user ? (
                <>
                  <Link
                    to="/login"
                    className="inline-flex items-center rounded-full px-4 py-2 text-sm font-medium text-slate-700 transition hover:bg-slate-100 hover:text-slate-900"
                  >
                    Login
                  </Link>
                  <Link
                    to="/register"
                    className="inline-flex items-center rounded-full bg-slate-950 px-4 py-2 text-sm font-medium text-white transition hover:bg-cyan-600"
                  >
                    Register
                  </Link>
                </>
              ) : null}
              <Link
                to="/feedback"
                className="inline-flex items-center rounded-full px-4 py-2 text-sm font-medium text-slate-700 transition hover:bg-slate-100 hover:text-slate-900"
              >
                Feedback
              </Link>
              {roleDetails?.user && (
                <button
                  type="button"
                  onClick={handleLogout}
                  className="inline-flex items-center rounded-full border border-slate-200 bg-white px-4 py-2 text-sm font-medium text-slate-700 transition hover:bg-slate-50"
                >
                  Sign out
                </button>
              )}
            </div>

            <div className="flex items-center lg:hidden">
              <button
                type="button"
                onClick={() => setIsMobileMenuOpen((prev) => !prev)}
                className="inline-flex items-center justify-center rounded-full border border-slate-200 bg-white p-2 text-slate-700 transition hover:bg-slate-100 hover:text-slate-900"
                aria-expanded={isMobileMenuOpen}
                aria-label="Toggle navigation menu"
              >
                {isMobileMenuOpen ? (
                  <X className="h-6 w-6" aria-hidden="true" />
                ) : (
                  <Menu className="h-6 w-6" aria-hidden="true" />
                )}
              </button>
            </div>
          </div>
        </div>

        {isMobileMenuOpen && createPortal(
          <div className="fixed inset-0 z-[1300] bg-slate-950/45 backdrop-blur-sm lg:hidden" onClick={closeMobileMenu}>
            <div
              className="ml-auto flex h-dvh w-full max-w-[420px] flex-col border-l border-slate-200 bg-slate-50 shadow-2xl"
              onClick={(event) => event.stopPropagation()}
            >
              <div className="flex min-h-[86px] items-center justify-between gap-4 border-b border-slate-200 bg-white px-4 py-3">
                <BrandLogo compact to="/" />
                <button
                  type="button"
                  onClick={closeMobileMenu}
                  className="inline-flex h-12 w-12 shrink-0 items-center justify-center rounded-full border border-slate-200 bg-white text-slate-700 transition hover:bg-slate-100"
                  aria-label="Close navigation menu"
                >
                  <X className="h-6 w-6" aria-hidden="true" />
                </button>
              </div>

              <div className="border-b border-slate-200 bg-white px-4 py-4">
                <p className="text-xs font-semibold uppercase tracking-[0.18em] text-slate-500">Menu</p>
                <p className="mt-1 text-lg font-semibold text-slate-950">LocalAlly services</p>
              </div>

              <div className="flex-1 space-y-3 overflow-y-auto overscroll-contain px-4 py-4">
                {!roleDetails?.user ? (
                  <>
                    <div className="grid grid-cols-2 gap-3">
                      <Link
                        to="/login"
                        className="inline-flex min-h-[46px] items-center justify-center rounded-lg border border-slate-200 bg-white px-3 py-2 text-sm font-semibold text-slate-800 transition hover:bg-slate-50"
                        onClick={closeMobileMenu}
                      >
                        Login
                      </Link>
                      <Link
                        to="/register"
                        className="inline-flex min-h-[46px] items-center justify-center rounded-lg bg-slate-950 px-3 py-2 text-sm font-semibold text-white transition hover:bg-cyan-600"
                        onClick={closeMobileMenu}
                      >
                        Register
                      </Link>
                    </div>
                    <Link
                      to="/"
                      className={`${location.pathname === '/' ? 'bg-slate-950 text-white' : 'bg-white text-slate-900 hover:bg-slate-50'} block rounded-xl border border-slate-200 px-4 py-3 text-sm font-semibold transition`}
                      onClick={closeMobileMenu}
                    >
                      Home
                    </Link>
                    {publicMenuGroups.map((group) => (
                      <MobileMenuGroup
                        key={group.label}
                        group={group}
                        pathname={location.pathname}
                        isOpen={openMobileGroup === group.label}
                        onToggle={() => setOpenMobileGroup((current) => (current === group.label ? '' : group.label))}
                        onNavigate={closeMobileMenu}
                      />
                    ))}
                  </>
                ) : (
                  <>
                    <Link
                      to={dashboardPath}
                      className="block rounded-xl border border-slate-200 bg-white px-4 py-3 text-sm font-semibold text-slate-900 transition hover:bg-slate-50"
                      onClick={closeMobileMenu}
                    >
                      Dashboard
                    </Link>
                    {isRidesModuleEnabled && (
                      <Link
                        to="/rides/book"
                        className="block rounded-xl border border-teal-200 bg-teal-50 px-4 py-3 text-sm font-semibold text-teal-800 transition hover:bg-teal-100"
                        onClick={closeMobileMenu}
                      >
                        Book Ride
                      </Link>
                    )}
                  </>
                )}
              </div>

              <div className="border-t border-slate-200 bg-white p-4">
                <Link
                  to="/feedback"
                  className="inline-flex min-h-[46px] w-full items-center justify-center rounded-lg border border-slate-200 bg-white px-3 py-2 text-sm font-semibold text-slate-800 transition hover:bg-slate-50"
                  onClick={closeMobileMenu}
                >
                  Feedback
                </Link>
                {roleDetails?.user ? (
                  <button
                    type="button"
                    onClick={handleLogout}
                    className="mt-3 inline-flex min-h-[46px] w-full items-center justify-center rounded-lg bg-slate-950 px-3 py-2 text-sm font-semibold text-white transition hover:bg-slate-800"
                  >
                    Sign out
                  </button>
                ) : null}
              </div>
            </div>
          </div>,
          document.body,
        )}
      </nav>

      <main id="main-content" tabIndex={-1} className="focus:outline-none">
        <Outlet />
      </main>
      <SharedFooter />
    </div>
  );
};

export default PublicLayout;
